import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { productsApi as P, inr } from "@/lib/products";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Package, History } from "lucide-react";

const EMPTY = { item_name: "", category: "", description: "", width: "", height: "", size_unit: "inch", fabric_required: "", selling_price: "", gst_pct: 5, unit: "Nos" };

export default function Products() {
  const { can } = useAuth();
  const canCreate = can("products", "create");
  const canEdit = can("products", "edit");
  const [meta, setMeta] = useState({ categories: [], units: [], size_units: [], gst_rates: [] });
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    P.list({ status: status === "all" ? undefined : status, category: category === "all" ? undefined : category, search: search || undefined })
      .then(setRows).finally(() => setLoading(false));
    P.stats().then(setStats);
  };
  useEffect(() => { P.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, category]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openNew = () => { setEditRow(null); setOpen(true); };
  const openEdit = (r) => { setEditRow(r); setOpen(true); };
  const toggle = async (r) => {
    try { await P.setStatus(r.id, !r.is_active); toast.success(r.is_active ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Product Master</h2>
          <p className="mt-1 text-sm text-slate-500">The single source of truth for products you manufacture &amp; sell. Used by Quotations, Sales Orders &amp; Production Planning.</p>
        </div>
        {canCreate && <Button onClick={openNew} data-testid="add-product-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />Add Product</Button>}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi testid="prod-stat-total" label="Products" value={stats.total ?? 0} />
        <Kpi testid="prod-stat-active" label="Active" value={stats.active ?? 0} accent="text-emerald-600" />
        <Kpi testid="prod-stat-inactive" label="Inactive" value={stats.inactive ?? 0} accent="text-slate-500" />
        <Kpi testid="prod-stat-cats" label="Categories" value={stats.categories ?? 0} accent="text-indigo-600" />
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / code" className="flex-1 min-w-[200px]" data-testid="product-search" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48" data-testid="product-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="product-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
          </Select>
        </div>
        {loading ? <Spin /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <Th>Code</Th><Th>Item</Th><Th>Category</Th><Th>Size</Th><Th>Area</Th><Th>Fabric (m)</Th><Th>Price</Th><Th>GST</Th><Th>Unit</Th><Th>Status</Th><Th></Th>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`product-row-${r.item_code}`}>
                    <TableCell className="font-medium text-slate-800 cursor-pointer" onClick={() => setDetail(r.id)}>{r.item_code}</TableCell>
                    <TableCell className="text-slate-700">{r.item_name}</TableCell>
                    <TableCell className="text-slate-600">{r.category || "—"}</TableCell>
                    <TableCell className="text-slate-600">{r.width && r.height ? `${r.width} × ${r.height} ${r.size_unit}` : "—"}</TableCell>
                    <TableCell className="text-slate-600 tabular-nums">{r.area ? r.area : "—"}</TableCell>
                    <TableCell className="text-slate-600 tabular-nums">{r.fabric_required || "—"}</TableCell>
                    <TableCell className="text-slate-800 tabular-nums">{inr(r.selling_price)}</TableCell>
                    <TableCell className="text-slate-600 tabular-nums">{r.gst_pct}%</TableCell>
                    <TableCell className="text-slate-600">{r.unit}</TableCell>
                    <TableCell><Badge className={r.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{r.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                    <TableCell className="text-right">
                      {canEdit && <span className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-product-${r.item_code}`}>Edit</Button>
                        <Button size="sm" variant="outline" onClick={() => toggle(r)} data-testid={`toggle-product-${r.item_code}`}>{r.is_active ? "Deactivate" : "Activate"}</Button>
                      </span>}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={11} className="py-12 text-center text-slate-400" data-testid="products-empty">No products yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <ProductDialog open={open} onOpenChange={setOpen} meta={meta} row={editRow} onDone={load} />
      <ProductDetail pid={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

const Kpi = ({ label, value, accent = "text-slate-900", testid }) => (
  <Card className="p-4" data-testid={testid}><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className={`mt-1 font-heading text-xl font-bold ${accent}`}>{value}</p></Card>
);
const Th = ({ children }) => <TableHead className="text-xs uppercase text-slate-500">{children}</TableHead>;
const Spin = () => <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

function ProductDialog({ open, onOpenChange, meta, row, onDone }) {
  const editing = !!row;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(row ? { ...EMPTY, ...row } : EMPTY); }, [open, row]);
  const area = (Number(f.width) || 0) * (Number(f.height) || 0);
  const submit = async () => {
    if (!f.item_name) { toast.error("Item name required"); return; }
    setBusy(true);
    try {
      const payload = {
        item_name: f.item_name, category: f.category || null, description: f.description || null,
        width: f.width === "" ? null : Number(f.width), height: f.height === "" ? null : Number(f.height),
        size_unit: f.size_unit || "inch", fabric_required: Number(f.fabric_required) || 0,
        selling_price: Number(f.selling_price) || 0, gst_pct: Number(f.gst_pct) || 0, unit: f.unit || "Nos",
      };
      if (editing) await P.update(row.id, payload); else await P.create(payload);
      toast.success(editing ? "Product updated" : "Product created");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5 text-indigo-600" />{editing ? `Edit ${row.item_code}` : "Add Product"}</DialogTitle>
          <DialogDescription>Area is auto-calculated from width × height. Fabric required is per unit.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Item name *</Label><Input value={f.item_name} onChange={(e) => setF({ ...f, item_name: e.target.value })} data-testid="product-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Category</Label>
            <Select value={f.category || ""} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Unit</Label>
            <Select value={f.unit || "Nos"} onValueChange={(v) => setF({ ...f, unit: v })}><SelectTrigger data-testid="product-unit-select"><SelectValue /></SelectTrigger><SelectContent>{meta.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="product-desc-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Width</Label><Input type="number" value={f.width} onChange={(e) => setF({ ...f, width: e.target.value })} data-testid="product-width-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Height</Label><Input type="number" value={f.height} onChange={(e) => setF({ ...f, height: e.target.value })} data-testid="product-height-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Size unit</Label>
            <Select value={f.size_unit || "inch"} onValueChange={(v) => setF({ ...f, size_unit: v })}><SelectTrigger data-testid="product-sizeunit-select"><SelectValue /></SelectTrigger><SelectContent>{meta.size_units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Area (auto)</Label><Input value={area ? `${area.toFixed(2)} ${f.size_unit}²` : "—"} disabled data-testid="product-area-display" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Fabric required (m / unit)</Label><Input type="number" value={f.fabric_required} onChange={(e) => setF({ ...f, fabric_required: e.target.value })} data-testid="product-fabric-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Selling price (₹)</Label><Input type="number" value={f.selling_price} onChange={(e) => setF({ ...f, selling_price: e.target.value })} data-testid="product-price-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">GST %</Label>
            <Select value={String(f.gst_pct)} onValueChange={(v) => setF({ ...f, gst_pct: Number(v) })}><SelectTrigger data-testid="product-gst-select"><SelectValue /></SelectTrigger><SelectContent>{meta.gst_rates.map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-product-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save" : "Add")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductDetail({ pid, onClose }) {
  const [p, setP] = useState(null);
  useEffect(() => { if (pid) P.get(pid).then(setP); else setP(null); }, [pid]);
  if (!pid) return null;
  return (
    <Dialog open={!!pid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        {!p ? (<><DialogHeader><DialogTitle>Loading product…</DialogTitle></DialogHeader><Spin /></>) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">{p.item_code} · {p.item_name}
                <Badge className={p.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{p.is_active ? "Active" : "Inactive"}</Badge>
              </DialogTitle>
              <DialogDescription>{p.category || "—"} · {p.unit}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Stat l="Size" v={p.width && p.height ? `${p.width} × ${p.height} ${p.size_unit}` : "—"} />
              <Stat l="Area" v={p.area ? `${p.area} ${p.size_unit}²` : "—"} />
              <Stat l="Fabric / unit" v={p.fabric_required ? `${p.fabric_required} m` : "—"} />
              <Stat l="Selling price" v={inr(p.selling_price)} />
              <Stat l="GST" v={`${p.gst_pct}%`} />
              <Stat l="Unit" v={p.unit} />
            </div>
            {p.description && <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">{p.description}</p>}
            <div className="mt-2">
              <h4 className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500"><History className="h-3.5 w-3.5" />Audit log</h4>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {(p.audit || []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-1.5 text-xs" data-testid={`audit-${a.id}`}>
                    <span className="capitalize text-slate-700">{a.action} — {a.detail}</span>
                    <span className="text-slate-400">{a.by} · {new Date(a.at).toLocaleString("en-IN")}</span>
                  </div>
                ))}
                {(p.audit || []).length === 0 && <p className="py-2 text-center text-xs text-slate-400">No audit entries.</p>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ l, v }) => <div className="rounded-md border border-slate-200 p-3"><p className="text-xs uppercase text-slate-500">{l}</p><p className="mt-1 font-heading text-lg font-bold text-slate-900">{v}</p></div>;
